import { useMemo, useCallback } from 'react';

import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import {
	useAvailabilityCheckContext,
	RequestType,
} from '@vakantiesnl/services/src/context/useAvailabilityCheckContext';
import { buildAvailabilityRequestBody } from '@vakantiesnl/services/src/dynamic-booking';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { useGetFinalOffer, usePartyComposition } from '@vakantiesnl/services/src/stores';
import { getTotalPartyCounts } from '@vakantiesnl/services/src/util/partyUtils';
import { type AccommodationOnlyOffer, type OfferItem } from '@vakantiesnl/types/src/search';

type Props = {
	onClick: () => void;
	gtmLabel: string;
};

export function useAccoAvailabilityCheck({ onClick, gtmLabel }: Props): {
	checkAvailability: (offer?: OfferItem | AccommodationOnlyOffer) => void;
} {
	const { accommodation } = useGetAccommodationByEntityId();
	const currentOffer = useGetFinalOffer(accommodation);
	const party = usePartyComposition();
	const totalPartyComposition = useMemo(() => getTotalPartyCounts(party), [party]);
	const microCopies = useMicroCopyContext();

	const {
		api: { setAvailabilityRequestBody, setAdditionalGTMData },
	} = useAvailabilityCheckContext();

	const checkAvailability = useCallback(
		(offer?: OfferItem | AccommodationOnlyOffer) => {
			setAdditionalGTMData({ label: gtmLabel });
			onClick();

			const offerToUse = offer || currentOffer;

			const body =
				offerToUse && totalPartyComposition
					? buildAvailabilityRequestBody(offerToUse, accommodation, totalPartyComposition, party, microCopies)
					: undefined;

			const oldPrice = offerToUse?.priceTotal;
			setAvailabilityRequestBody(
				oldPrice && body
					? {
							requestBody: body,
							requestType: RequestType.OFFER_AVAILABILITY,
							oldPrice,
						}
					: undefined,
			);
		},
		[
			setAdditionalGTMData,
			gtmLabel,
			onClick,
			currentOffer,
			totalPartyComposition,
			accommodation,
			party,
			microCopies,
			setAvailabilityRequestBody,
		],
	);

	return { checkAvailability };
}
